
import React from 'react';
import { X, Plus, Trash2, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Modal } from '../../../components/ui';
import { FormField, Input, Textarea } from '../../../components/ui/FormComponents';
import api from '../../../services/api';
import toast from 'react-hot-toast';

// ── Default blank item ──────────────────────────────────────────────────────
const blankItem = () => ({
  _key: Math.random().toString(36).slice(2),
  category: '', subCategory: '', articleId: '',
  description: '', information: '',
  internalType: '', shutterType: '',
  width: '', height: '', depth: '',
  uom: '', moq: '', quantity: '',
  blumPrice: '', vervePrice: '',
  gstPercentage: 18,
  image: null, remark: ''
});

// ── Simple label helper ─────────────────────────────────────────────────────
const FL = ({ text, req }) => (
  <span className="text-slate-700 font-bold text-[11px] tracking-wide uppercase">
    {text}{req && <span className="text-red-500 ml-0.5">*</span>}
  </span>
);

// ── Format comma numbers ────────────────────────────────────────────────────
const formatCommas = (val) => {
  if (val === undefined || val === null || val === '') return '';
  let clean = String(val).replace(/[^\d.]/g, '');
  const parts = clean.split('.');
  if (parts.length > 2) clean = parts[0] + '.' + parts.slice(1).join('');
  let intPart = parts[0];
  if (intPart) {
    let lastThree = intPart.substring(intPart.length - 3);
    let other = intPart.substring(0, intPart.length - 3);
    if (other !== '') lastThree = ',' + lastThree;
    intPart = other.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
  }
  return parts.length > 1 ? `${intPart}.${parts[1]}` : intPart;
};

const parsePrice = (val) =>
  val && typeof val === 'string' ? Number(val.replace(/,/g, '')) : (Number(val) || 0);

// ── Reusable select style ───────────────────────────────────────────────────
const selCls = "w-full border border-slate-200 rounded-lg px-3 py-2.5 text-[13px] font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue transition-all";

/**
 * OpenStockEntryModal — supports multiple items per purchase order.
 * Header: Vendor + Store (shared across all items).
 * Items: a dynamic list, each with its own product fields.
 */
export const OpenStockEntryModal = ({
  isOpen,
  onClose,
  form,
  setForm,
  dynamicSettings = {},
  loading = false,
  refreshAll
}) => {
  const { suppliers = [], stores = [], units = [] } = dynamicSettings;

  // ── Live master data ───────────────────────────────────────────────────
  const [vendors, setVendors] = React.useState([]);
  const [liveStores, setLiveStores] = React.useState([]);
  const [liveUnits, setLiveUnits] = React.useState([]);

  // ── Header state (shared for all items) ────────────────────────────────
  const [header, setHeader] = React.useState({ vendorName: '', store: '', invoiceDate: '', version: '' });

  // ── Items list ─────────────────────────────────────────────────────────
  const [items, setItems] = React.useState([blankItem()]);

  // ── Collapsed state — key → bool ───────────────────────────────────────
  const [collapsed, setCollapsed] = React.useState({});

  // ── Fetch master data on open ──────────────────────────────────────────
  React.useEffect(() => {
    if (!isOpen) return;

    api.get('/inventory/vendors')
      .then(res => { const d = res.data?.data || res.data || []; setVendors(Array.isArray(d) ? d : []); })
      .catch(() => {});

    api.get('/inventory/stores')
      .then(res => { const d = res.data?.data || res.data || []; setLiveStores(Array.isArray(d) ? d : []); })
      .catch(() => {});

    api.get('/inventory/units')
      .then(res => { const d = res.data?.data || res.data || []; setLiveUnits(Array.isArray(d) ? d : []); })
      .catch(() => {});

    // Auto-fill date & version for new entries
    if (form && !form._id) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      setHeader(prev => ({
        vendorName: form.vendorName || form.vendor || form.supplier || '',
        store: form.store || '',
        invoiceDate: form.invoiceDate || `${yyyy}-${mm}-${dd}`,
        version: form.version || '',
      }));
      // If editing existing item, pre-fill single item row
      if (form._id) {
        setItems([{ ...blankItem(), ...form, _key: form._id }]);
      } else {
        setItems([blankItem()]);
      }
    } else if (form?._id) {
      setHeader({
        vendorName: form.vendorName || form.vendor || form.supplier || '',
        store: form.store || '',
        invoiceDate: (form.invoiceDate && !isNaN(new Date(form.invoiceDate).valueOf())) ? new Date(form.invoiceDate).toISOString().split('T')[0] : '',
        version: form.version || '',
      });
      setItems([{ ...blankItem(), ...form, _key: form._id }]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // ── Item helpers ─────────────────────────────────────────────────────
  const updateItem = (key, field, val) =>
    setItems(prev => prev.map(it => it._key === key ? { ...it, [field]: val } : it));

  const addItem = () => {
    const ni = blankItem();
    setItems(prev => [...prev, ni]);
    // new item starts expanded
    setCollapsed(prev => ({ ...prev, [ni._key]: false }));
  };

  const removeItem = (key) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter(it => it._key !== key));
    setCollapsed(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const toggleCollapse = (key) =>
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const collapseAll = () =>
    setCollapsed(items.reduce((acc, it) => ({ ...acc, [it._key]: true }), {}));

  const expandAll = () =>
    setCollapsed(items.reduce((acc, it) => ({ ...acc, [it._key]: false }), {}));

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!header.vendorName) { toast.error('Please select a vendor'); return; }
    if (!header.store) { toast.error('Please select a store'); return; }
    if (!header.version || header.version.trim() === '') { toast.error('Please enter an Invoice Number'); return; }
    if (items.every(it => !it.category && !it.description && !it.articleId)) {
      toast.error('Please fill at least one item'); return;
    }

    const payload = items
      .filter(it => it.category || it.description || it.articleId)
      .map(({ _key, ...it }) => {
        const blumP = parsePrice(it.blumPrice);
        const qty = Number(it.quantity) || 0;
        const taxableAmount = blumP * qty;
        const gstP = Number(it.gstPercentage) || 0;
        const taxAmount = (taxableAmount * gstP) / 100;
        const netAmount = taxableAmount + taxAmount;

        return {
          ...it,
          ...header,
          blumPrice: blumP,
          vervePrice: parsePrice(it.vervePrice),
          moq: Number(it.moq) || undefined,
          quantity: qty || undefined,
          gstPercentage: gstP,
          taxableAmount,
          taxAmount,
          netAmount,
        };
      });

    try {

      if (form?._id && payload.length === 1) {
        await api.put(`/inventory/opening-stock/${form._id}`, payload[0]);
        toast.success('Closing Stock Entry Updated successfully!');
      } else {
        await api.post('/inventory/opening-stock/bulk', payload);
        toast.success(`${payload.length} Closing Stock Entry Saved successfully!`);
      }
      onClose();
      if (refreshAll) refreshAll();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save closing stock entry');
    }
  };

  const vendorList = vendors.length > 0 ? vendors : suppliers;
  const storeList = liveStores.length > 0 ? liveStores : stores;

  // ── Calculate Invoice Totals ──────────────────────────────────────────
  const invoiceTotals = items.reduce((acc, it) => {
    const qty = Number(it.quantity) || 0;
    const rate = parsePrice(it.blumPrice);
    const gstP = Number(it.gstPercentage) || 0;
    
    const taxable = qty * rate;
    const tax = (taxable * gstP) / 100;
    const net = taxable + tax;
    
    return {
      taxable: acc.taxable + taxable,
      tax: acc.tax + tax,
      net: acc.net + net
    };
  }, { taxable: 0, tax: 0, net: 0 });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-[820px] w-full"
      title={
        <div className="flex flex-col gap-0.5 z-10 relative">
          <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">VERVE KITCHENS</span>
          <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">Record Closing Stock</span>
        </div>
      }
    >
      <div className="flex flex-col max-h-[88vh]">
        <div className="flex-1 overflow-y-auto pr-1 pb-4 pt-4 space-y-5">

          {/* ── Closing Stock Header ─────────────────────────────────────────── */}
          <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Closing Stock Header</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Vendor */}
              <FormField label={<FL text="Vendor" req />}>
                <select
                  value={header.vendorName}
                  onChange={e => setHeader(h => ({ ...h, vendorName: e.target.value }))}
                  className={selCls}
                >
                  <option value="">Select vendor...</option>
                  {vendorList.map(s => {
                    const name = s.vendorName || s.name || s.title || '';
                    return <option key={s._id || name} value={name}>{name}</option>;
                  })}
                </select>
              </FormField>

              {/* Store */}
              <FormField label={<FL text="Store" req />}>
                <select
                  value={header.store}
                  onChange={e => setHeader(h => ({ ...h, store: e.target.value }))}
                  className={selCls}
                >
                  <option value="">Select store...</option>
                  {storeList.map(s => {
                    const name = s.storeName || s.name || s.title || '';
                    const id = s._id || name;
                    return <option key={id} value={name}>{name}</option>;
                  })}
                </select>
              </FormField>

              {/* Invoice Number */}
              <FormField label={<FL text="Invoice Number" req />}>
                <Input
                  type="text"
                  value={header.version}
                  placeholder="Enter Invoice No."
                  onChange={e => setHeader(h => ({ ...h, version: e.target.value }))}
                />
              </FormField>

              {/* Date */}
              <FormField label={<FL text="Invoice Date" />}>
                <Input
                  type="date"
                  value={header.invoiceDate}
                  onChange={e => setHeader(h => ({ ...h, invoiceDate: e.target.value }))}
                />
              </FormField>

            </div>
          </div>

          {/* ── Items List ──────────────────────────────────────────────── */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Items <span className="ml-2 bg-brand-blue text-white text-[9px] font-black px-2 py-0.5 rounded-full">{items.length}</span>
              </div>
              <div className="flex items-center gap-2">
                {items.length > 1 && (
                  <>
                    <button type="button" onClick={expandAll}
                      className="text-[10px] font-bold text-slate-500 hover:text-brand-blue transition-colors">
                      Expand All
                    </button>
                    <span className="text-slate-300">|</span>
                    <button type="button" onClick={collapseAll}
                      className="text-[10px] font-bold text-slate-500 hover:text-brand-blue transition-colors">
                      Collapse All
                    </button>
                    <span className="text-slate-300">|</span>
                  </>
                )}
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center gap-1.5 text-[11px] font-bold text-brand-blue hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-lg transition-all active:scale-95"
                >
                  <Plus size={13} /> Add Item
                </button>
              </div>
            </div>

            {items.map((item, idx) => (
              <div
                key={item._key}
                className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
              >
                {/* Item header bar */}
                <div
                  className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-100 cursor-pointer select-none"
                  onClick={() => toggleCollapse(item._key)}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-brand-blue text-white text-[10px] font-black flex items-center justify-center">{idx + 1}</div>
                    <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">
                      {item.description || item.articleId || item.category || `Item ${idx + 1}`}
                    </span>
                    {collapsed[item._key] && (item.blumPrice || item.vervePrice) && (
                      <span className="text-[10px] text-emerald-600 font-bold ml-2">
                        ₹{formatCommas(item.blumPrice) || formatCommas(item.vervePrice)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); removeItem(item._key); }}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-all"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                    <div className="text-slate-400 p-1">
                      {collapsed[item._key] ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
                    </div>
                  </div>
                </div>

                {/* Item fields — hidden when collapsed */}
                {!collapsed[item._key] && (
                <div className="p-4 space-y-4">
                  {/* Row 1: Category + Sub Category */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label={<FL text="Category" />}>
                      <Input value={item.category} onChange={e => updateItem(item._key, 'category', e.target.value)} placeholder="Enter category" />
                    </FormField>
                    <FormField label={<FL text="Sub Category" />}>
                      <Input value={item.subCategory} onChange={e => updateItem(item._key, 'subCategory', e.target.value)} placeholder="Enter sub category" />
                    </FormField>
                  </div>

                  {/* Row 2: Article ID + Internal Type */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label={<FL text="Article ID" />}>
                      <Input value={item.articleId} onChange={e => updateItem(item._key, 'articleId', e.target.value)} placeholder="Enter article ID" />
                    </FormField>
                    <FormField label={<FL text="Shutter Type" />}>
                      <Input value={item.shutterType} onChange={e => updateItem(item._key, 'shutterType', e.target.value)} placeholder="Enter shutter type" />
                    </FormField>
                  </div>

                  {/* Row 3: Description + Information */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label={<FL text="Description" />}>
                      <Textarea value={item.description} onChange={e => updateItem(item._key, 'description', e.target.value)} rows={2} placeholder="Enter description" />
                    </FormField>
                    <FormField label={<FL text="Information" />}>
                      <Textarea value={item.information} onChange={e => updateItem(item._key, 'information', e.target.value)} rows={2} placeholder="Enter information" />
                    </FormField>
                  </div>

                  {/* Row 4: Internal Type + Shutter Type */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label={<FL text="Internal Type" />}>
                      <Input value={item.internalType} onChange={e => updateItem(item._key, 'internalType', e.target.value)} placeholder="Enter internal type" />
                    </FormField>
                    <FormField label={<FL text="Remarks" />}>
                      <Input value={item.remark} onChange={e => updateItem(item._key, 'remark', e.target.value)} placeholder="Enter remarks" />
                    </FormField>
                  </div>

                  {/* Row 5: Dimensions + UOM + MOQ + Qty */}
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                    <FormField label={<FL text="Width" />}>
                      <Input value={item.width} onChange={e => updateItem(item._key, 'width', e.target.value)} placeholder="W" />
                    </FormField>
                    <FormField label={<FL text="Height" />}>
                      <Input value={item.height} onChange={e => updateItem(item._key, 'height', e.target.value)} placeholder="H" />
                    </FormField>
                    <FormField label={<FL text="Depth" />}>
                      <Input value={item.depth} onChange={e => updateItem(item._key, 'depth', e.target.value)} placeholder="D" />
                    </FormField>
                    <FormField label={<FL text="UOM" />}>
                      <select
                        value={item.uom}
                        onChange={e => updateItem(item._key, 'uom', e.target.value)}
                        className={selCls}
                      >
                        <option value="">Select UOM...</option>
                        {(liveUnits.length > 0 ? liveUnits : units).map((u) => {
                          const name = u.unitName || u.name || u.title || '';
                          const id = u._id || name;
                          return <option key={id} value={name}>{name}</option>;
                        })}
                      </select>
                    </FormField>
                    <FormField label={<FL text="MOQ" />}>
                      <Input type="number" value={item.moq} onChange={e => updateItem(item._key, 'moq', e.target.value)} placeholder="Min" />
                    </FormField>
                    <FormField label={<FL text="Qty" req />}>
                      <Input type="number" value={item.quantity} onChange={e => updateItem(item._key, 'quantity', e.target.value)} placeholder="Qty" />
                    </FormField>
                  </div>

                  {/* Row 6: Prices + GST + Image */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <FormField label={<FL text="Purchase Amount (₹)" />}>
                      <Input type="text" value={formatCommas(item.blumPrice)} onChange={e => updateItem(item._key, 'blumPrice', formatCommas(e.target.value))} placeholder="0.00" />
                    </FormField>
                    <FormField label={<FL text="Verve Price (₹)" />}>
                      <Input type="text" value={formatCommas(item.vervePrice)} onChange={e => updateItem(item._key, 'vervePrice', formatCommas(e.target.value))} placeholder="0.00" />
                    </FormField>
                    <FormField label={<FL text="GST %" />}>
                      <select
                        value={item.gstPercentage}
                        onChange={e => updateItem(item._key, 'gstPercentage', Number(e.target.value))}
                        className={selCls}
                      >
                        <option value={0}>0%</option>
                        <option value={5}>5%</option>
                        <option value={12}>12%</option>
                        <option value={18}>18%</option>
                        <option value={28}>28%</option>
                      </select>
                    </FormField>
                    <FormField label={<FL text="Image" />}>
                      {!item.image ? (
                        <input
                          type="file"
                          accept="image/*"
                          onChange={e => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => updateItem(item._key, 'image', reader.result);
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="block w-full text-xs text-slate-500 file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-[10px] file:font-bold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 file:cursor-pointer border border-slate-200 rounded-lg p-1 bg-white"
                        />
                      ) : (
                        <div className="relative w-12 h-12 group">
                          <img src={item.image} alt="Preview" className="w-full h-full object-cover rounded-lg border border-slate-200 shadow-sm" />
                          <button type="button" onClick={() => updateItem(item._key, 'image', null)}
                            className="absolute -top-1 -right-1 bg-white text-slate-600 hover:text-red-500 border border-slate-200 rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-all">
                            <X size={10} />
                          </button>
                        </div>
                      )}
                    </FormField>
                  </div>
                  
                  {/* Calculated GST Summary */}
                  {(() => {
                    const qty = Number(item.quantity) || 0;
                    const rate = parsePrice(item.blumPrice);
                    const gstP = Number(item.gstPercentage) || 0;
                    const taxable = qty * rate;
                    const tax = (taxable * gstP) / 100;
                    const net = taxable + tax;
                    return (
                      <div className="bg-emerald-50/50 border border-emerald-100 rounded-lg p-3 flex items-center justify-between mt-2">
                        <div className="flex flex-col">
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Taxable (Gross)</span>
                          <span className="text-[12px] font-semibold text-slate-700">₹{formatCommas(taxable.toFixed(2))}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">GST ({gstP}%)</span>
                          <span className="text-[12px] font-semibold text-slate-700">₹{formatCommas(tax.toFixed(2))}</span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">Net Amount</span>
                          <span className="text-[14px] font-black text-emerald-600 tracking-tight">₹{formatCommas(net.toFixed(2))}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                )}
              </div>
            ))}

          </div>

        </div>

        {/* ── Footer Actions ─────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-100 mt-2 pb-2">
          
          <div className="flex items-center gap-4 text-slate-700">
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total Taxable</span>
              <span className="text-[12px] font-semibold">₹{formatCommas(invoiceTotals.taxable.toFixed(2))}</span>
            </div>
            <div className="w-px h-8 bg-slate-200"></div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total GST</span>
              <span className="text-[12px] font-semibold">₹{formatCommas(invoiceTotals.tax.toFixed(2))}</span>
            </div>
            <div className="w-px h-8 bg-slate-200"></div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">Net Amount</span>
              <span className="text-[16px] font-black text-emerald-600 tracking-tight leading-none">
                ₹{formatCommas(invoiceTotals.net.toFixed(2))}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-[400px]">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-lg text-[12px] font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="flex-[2] cursor-pointer py-2.5 bg-slate-900 hover:bg-black text-white rounded-lg text-[12px] font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
          >
            <CheckCircle2 size={15} />
            Submit
          </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
